import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DialogSection, DialogField, DialogOption } from '@/lib/stores/dialogStore';
import { Editor, Block, CustomBlockDefinition } from '@/components/editor/Editor';
import { getCachedTypes, TypeElement } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useDisplayLanguageOptional, resolveOptionDisplayText } from '@/contexts/DisplayLanguageContext';
import { DialogSectionBlock } from './DialogSectionBlock';
import { ScreenplayPagination } from './ScreenplayPagination';
import { Heading } from 'lucide-react';
import './screenplay-styles.css';

interface DialogWriteViewProps {
  sections: DialogSection[];
  speakerMode?: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  gameId?: string;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  onUpdateSection: (sectionIndex: number, updates: Partial<DialogSection>) => void;
}

export function DialogWriteView({
  sections,
  speakerMode = 'none',
  speakerTypeId,
  speakerTemplateIds,
  gameId,
  onUpdateField,
  onUpdateOption,
  onUpdateSection,
}: DialogWriteViewProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;

  // Get data for speaker display
  const types = useMemo(() => 
    effectiveGameId ? getCachedTypes(effectiveGameId) : []
  , [effectiveGameId]);
  
  const objects = useMemo(() => 
    effectiveGameId ? getCachedObjects(effectiveGameId) : []
  , [effectiveGameId]);
  
  // Get type elements if speaker mode is 'type'
  const typeElements = useMemo(() => {
    const selectedType = speakerMode === 'type' && speakerTypeId 
      ? types.find(t => t.id === speakerTypeId)
      : null;
    return selectedType?.data || [];
  }, [speakerMode, speakerTypeId, types]);
  
  // Get objects from selected templates if speaker mode is 'object'
  const availableObjects = useMemo(() => {
    if (speakerMode === 'object' && speakerTemplateIds && speakerTemplateIds.length > 0) {
      // Filter objects: must have blueprint_id matching selected templates
      // AND should be glossary/library type (not document type) for speakers
      return objects.filter(obj => 
        obj.blueprint_id && 
        speakerTemplateIds.includes(obj.blueprint_id) &&
        (obj.type === 'glossary' || obj.type === 'Library Object' || !obj.type)
      );
    }
    return [];
  }, [speakerMode, speakerTemplateIds, objects]);

  // Helper to get speaker display name
  const getSpeakerName = useCallback((field: DialogField): string => {
    if (!field.speaker || speakerMode === 'none') return '';
    
    if (speakerMode === 'free-text') {
      return field.speaker;
    } else if (speakerMode === 'type') {
      const element = typeElements.find((e: TypeElement) => e.id === field.speaker);
      return element?.name || field.speaker;
    } else if (speakerMode === 'object') {
      const obj = availableObjects.find(o => o.id === field.speaker);
      return obj?.name || field.speaker;
    }
    
    return field.speaker;
  }, [speakerMode, typeElements, availableObjects]);

  // Convert dialog sections/fields to editor blocks
  const convertToBlocks = useCallback((): Block[] => {
    const blocks: Block[] = [];
    
    sections.forEach((section, sectionIndex) => {
      // Add section header using custom dialog-section block
      if (section.name && section.name !== `Section ${sectionIndex + 1}`) {
        blocks.push({
          id: `section-${section.id}`,
          type: 'dialog-section',
          content: section.name,
          metadata: {
            dialogSection: true,
            sectionId: section.id,
            sectionIndex,
          },
        });
      }

      // Add fields
      section.fields?.forEach((field, fieldIndex) => {
        const speakerName = getSpeakerName(field);

        if (field.type === 'context') {
          // Context as italic text
          blocks.push({
            id: `field-${field.id}`,
            type: 'text',
            content: field.text,
            metadata: {
              dialogField: true,
              sectionId: section.id,
              sectionIndex,
              fieldId: field.id,
              fieldType: 'context',
              style: 'context',
            },
          });
        } else if (field.type === 'dialog' || field.type === 'question' || field.type === 'end-dialog') {
          // Character name (if applicable)
          if (speakerMode !== 'none' && speakerName) {
            blocks.push({
              id: `speaker-${field.id}`,
              type: 'text',
              content: speakerName,
              metadata: {
                dialogSpeaker: true,
                sectionId: section.id,
                sectionIndex,
                fieldId: field.id,
                style: 'speaker',
              },
            });
          }

          // Dialog text
          blocks.push({
            id: `field-${field.id}`,
            type: 'text',
            content: field.text,
            metadata: {
              dialogField: true,
              sectionId: section.id,
              sectionIndex,
              fieldId: field.id,
              fieldType: field.type,
              style: field.type === 'dialog' ? 'dialog' : field.type === 'question' ? 'question' : 'end-dialog',
            },
          });

          // Options for questions
          if (field.type === 'question' && field.options) {
            field.options.forEach((option, optionIndex) => {
              blocks.push({
                id: `option-${field.id}-${optionIndex}`,
                type: 'text',
                content: `→ ${resolveOptionDisplayText(option, displayLanguage, effectiveGameId)}${option.next_section ? ` ↳ continues to "${option.next_section}"` : ''}`,
                metadata: {
                  dialogOption: true,
                  sectionId: section.id,
                  sectionIndex,
                  fieldId: field.id,
                  optionIndex,
                  style: 'option',
                },
              });
            });
          }
        } else if (field.type === 'end-text' || field.type === 'end-dialog') {
          // End dialog (centered, italic)
          blocks.push({
            id: `field-${field.id}`,
            type: 'text',
            content: field.text,
            metadata: {
              dialogField: true,
              sectionId: section.id,
              sectionIndex,
              fieldId: field.id,
              fieldType: 'end-text',
              style: 'end-text',
            },
          });
        }
      });

      // Add separator between sections (except for the last one)
      if (sectionIndex < sections.length - 1) {
        blocks.push({
          id: `separator-${section.id}`,
          type: 'text',
          content: '---',
          metadata: {
            dialogSeparator: true,
            style: 'separator',
          },
        });
      }
    });

    return blocks;
  }, [sections, speakerMode, getSpeakerName, displayLanguage, effectiveGameId]);

  const [blocks, setBlocks] = useState<Block[]>(() => convertToBlocks());
  const isInternalUpdateRef = useRef(false);
  const previousSectionsRef = useRef<string>();
  const previousDisplayLanguageRef = useRef<string | null>(displayLanguage);

  // Only update blocks when sections change from EXTERNAL sources
  // (like switching dialogs or changing modes), not from our own updates.
  // Also reconvert when display language changes (for translated option text).
  useEffect(() => {
    // Skip if this update came from our own handleBlocksChange
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    const sectionsString = JSON.stringify(sections);
    const languageChanged = displayLanguage !== previousDisplayLanguageRef.current;
    
    // Reconvert if sections actually changed OR display language changed
    if ((sectionsString !== previousSectionsRef.current && previousSectionsRef.current !== undefined) || languageChanged) {
      setBlocks(convertToBlocks());
    }
    
    previousSectionsRef.current = sectionsString;
    previousDisplayLanguageRef.current = displayLanguage;
  }, [sections, convertToBlocks, displayLanguage]);

  // Handle block changes and sync back to dialog structure
  const handleBlocksChange = useCallback((updatedBlocks: Block[]) => {
    // Update blocks state immediately for responsive UI
    setBlocks(updatedBlocks);

    // Mark this as an internal update to prevent circular updates
    isInternalUpdateRef.current = true;

    // Sync changes back to dialog structure
    updatedBlocks.forEach((block, blockIndex) => {
      // Skip speaker name blocks and separators - they're display-only
      if (block.metadata?.dialogSpeaker || block.metadata?.dialogSeparator) {
        return;
      }

      // Handle section name changes (both existing and new dialog-section blocks)
      if (block.type === 'dialog-section' || block.metadata?.dialogSection) {
        // If this is an existing section with sectionIndex
        if (block.metadata?.sectionIndex !== undefined) {
          const { sectionIndex } = block.metadata;
          const section = sections[sectionIndex];
          
          // Only update if the name actually changed
          if (section && block.content !== section.name) {
            onUpdateSection(sectionIndex, { name: block.content });
          }
        }
        // Note: New sections created via / menu will need to be handled separately
        // They don't have sectionIndex yet - that would require adding new sections to the dialog
      } 
      // Handle field text changes
      else if (block.metadata?.dialogField && block.metadata?.fieldId) {
        const { sectionIndex, fieldId } = block.metadata;
        const section = sections[sectionIndex];
        
        if (section) {
          const field = section.fields?.find(f => f.id === fieldId);
          
          // Only update if content actually changed
          if (field && block.content !== field.text) {
            onUpdateField(sectionIndex, fieldId, { text: block.content });
          }
        }
      } 
      // Handle option text changes
      else if (block.metadata?.dialogOption && block.metadata?.fieldId) {
        const { sectionIndex, fieldId, optionIndex } = block.metadata;
        
        // Extract option text (remove arrow and continuation text)
        const optionText = block.content
          .replace(/^→\s*/, '') // Remove arrow
          .replace(/\s*↳.*$/, ''); // Remove continuation text
        
        const section = sections[sectionIndex];
        if (section) {
          const field = section.fields?.find(f => f.id === fieldId);
          const option = field?.options?.[optionIndex];
          
          // Only update if content actually changed
          if (option && optionText !== option.option_text) {
            onUpdateOption(sectionIndex, fieldId, optionIndex, { option_text: optionText });
          }
        }
      }
    });
  }, [onUpdateField, onUpdateOption, onUpdateSection, sections]);

  // Define custom blocks for the editor
  const customBlocks: CustomBlockDefinition[] = useMemo(() => [
    {
      type: 'dialog-section',
      label: 'Dialog Section',
      icon: <Heading size={16} />,
      description: 'Add a section divider with title',
      component: DialogSectionBlock,
      initialMetadata: {
        dialogSection: true,
      },
    },
  ], []);

  return (
    <div className="w-full h-full flex flex-col bg-background">

      {/* Editor with screenplay-formatted blocks */}
      <div className="flex-1 overflow-y-auto screenplay-pages-container">
        {sections.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg mb-2">No content yet</p>
            <p className="text-gray-400 text-sm">Switch to Edit Mode to add sections and fields to your dialog.</p>
          </div>
        ) : (
          <ScreenplayPagination>
            <Editor
              initialBlocks={blocks}
              onChange={handleBlocksChange}
              gameId={effectiveGameId}
              customBlocks={customBlocks}
            />
          </ScreenplayPagination>
        )}
      </div>
    </div>
  );
}

